import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Card, Input, Textarea, Button, Select } from '../../../styles/components';

const FormContainer = styled(Card)`
  padding: 2rem;
  margin-bottom: 0;
  max-height: 80vh;
  overflow-y: auto;
  background: rgba(15, 23, 42, 0.85);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(99, 102, 241, 0.3);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  z-index: 101;
  border-radius: 16px;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(15, 23, 42, 0.2);
    border-radius: 10px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(99, 102, 241, 0.3);
    border-radius: 10px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(99, 102, 241, 0.5);
  }
`;

const FormTitle = styled.h2`
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  font-size: 1.75rem;
  border-bottom: 1px solid rgba(99, 102, 241, 0.2);
  position: relative;
  background: linear-gradient(
    to right,
    rgba(255, 255, 255, 0.95),
    rgba(99, 102, 241, 0.9)
  );
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 1px;
    background: linear-gradient(
      to right,
      rgba(99, 102, 241, 0.1),
      rgba(99, 102, 241, 0.5),
      rgba(99, 102, 241, 0.1)
    );
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const FormRow = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const HelperText = styled.p`
  margin-top: 0.25rem;
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const SectionTitle = styled.h3`
  margin: 2.5rem 0 1.5rem;
  padding-bottom: 0.75rem;
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.25rem;
  border-bottom: 1px solid rgba(99, 102, 241, 0.3);
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    width: 30%;
    height: 2px;
    background: linear-gradient(
      to right,
      rgba(99, 102, 241, 0.8),
      rgba(99, 102, 241, 0.2)
    );
  }
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
`;

const ArrayItemContainer = styled.div`
  background-color: rgba(0, 0, 0, 0.03);
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
  position: relative;
`;

const RemoveButton = styled.button`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.danger};
  cursor: pointer;
  font-size: 1.2rem;
  
  &:hover {
    color: ${({ theme }) => theme.colors.dangerDark};
  }
`;

const DateTimeInput = styled(Input)`
  /* Additional styling for datetime inputs */
`;

// Empty workshop template for creating new workshops
const emptyWorkshop = {
  title: '',
  startDateTime: new Date().toISOString(),
  endDateTime: new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString(), // 2 hours later
  location: '',
  meetingLink: '',
  shortDescription: '',
  longDescription: '',
  maxParticipants: 50,
  currentParticipants: 0,
  registrationDeadline: new Date().toISOString(),
  status: 'upcoming',
  speakers: [
    { name: '', title: '', bio: '', image: '' }
  ],
  agenda: [
    { time: '', activity: '' }
  ],
  materials: [
    { name: '', link: '' }
  ],
  tags: []
};

const WorkshopForm = ({ workshop, isEditing, onSave, onCancel }) => {
  // Initialize form data with workshop data or empty template
  const [formData, setFormData] = useState(workshop || emptyWorkshop);
  const [tagInput, setTagInput] = useState('');
  
  useEffect(() => {
    // Update form data when workshop prop changes
    setFormData(workshop || emptyWorkshop);
  }, [workshop]);
  
  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  // Handle date/time changes
  const handleDateTimeChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  // Handle speaker changes
  const handleSpeakerChange = (index, field, value) => {
    const updatedSpeakers = [...formData.speakers];
    updatedSpeakers[index] = { ...updatedSpeakers[index], [field]: value };
    setFormData(prev => ({ ...prev, speakers: updatedSpeakers }));
  };
  
  // Add a new speaker
  const addSpeaker = () => {
    setFormData(prev => ({
      ...prev,
      speakers: [...prev.speakers, { name: '', title: '', bio: '', image: '' }]
    }));
  };
  
  // Remove a speaker
  const removeSpeaker = (index) => {
    const updatedSpeakers = [...formData.speakers];
    updatedSpeakers.splice(index, 1);
    setFormData(prev => ({ ...prev, speakers: updatedSpeakers }));
  };
  
  // Handle agenda item changes
  const handleAgendaChange = (index, field, value) => {
    const updatedAgenda = [...formData.agenda];
    updatedAgenda[index] = { ...updatedAgenda[index], [field]: value };
    setFormData(prev => ({ ...prev, agenda: updatedAgenda }));
  };
  
  // Add a new agenda item
  const addAgendaItem = () => {
    setFormData(prev => ({
      ...prev,
      agenda: [...prev.agenda, { time: '', activity: '' }]
    }));
  };
  
  // Remove an agenda item
  const removeAgendaItem = (index) => {
    const updatedAgenda = [...formData.agenda];
    updatedAgenda.splice(index, 1);
    setFormData(prev => ({ ...prev, agenda: updatedAgenda }));
  };
  
  // Handle material changes
  const handleMaterialChange = (index, field, value) => {
    const updatedMaterials = [...formData.materials];
    updatedMaterials[index] = { ...updatedMaterials[index], [field]: value };
    setFormData(prev => ({ ...prev, materials: updatedMaterials }));
  };
  
  // Add a new material
  const addMaterial = () => {
    setFormData(prev => ({
      ...prev,
      materials: [...prev.materials, { name: '', link: '' }]
    }));
  };
  
  // Remove a material
  const removeMaterial = (index) => {
    const updatedMaterials = [...formData.materials];
    updatedMaterials.splice(index, 1);
    setFormData(prev => ({ ...prev, materials: updatedMaterials }));
  };
  
  // Handle tag input
  const handleTagInputChange = (e) => {
    setTagInput(e.target.value);
  };
  
  // Add a tag
  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim().toLowerCase())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim().toLowerCase()]
      }));
      setTagInput('');
    }
  };
  
  // Remove a tag
  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };
  
  // Handle tag input keydown (add tag on Enter)
  const handleTagKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    }
  };
  
  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate form data
    if (!formData.title || !formData.startDateTime || !formData.endDateTime || !formData.location) {
      alert('Please fill in all required fields: Title, Start Time, End Time, and Location');
      return;
    }
    
    // Ensure start time is before end time
    if (new Date(formData.startDateTime) >= new Date(formData.endDateTime)) {
      alert('Start time must be before end time');
      return;
    }
    
    // Save the workshop data
    onSave(formData);
  };
  
  return (
    <FormContainer>
      <FormTitle>{isEditing ? 'Edit Workshop' : 'Create New Workshop'}</FormTitle>
      
      <form onSubmit={handleSubmit}>
        <SectionTitle>Basic Information</SectionTitle>
        
        <FormGroup>
          <Label htmlFor="title">Workshop Title*</Label>
          <Input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            placeholder="e.g., Technical Interview Preparation"
            required
          />
        </FormGroup>
        
        <FormRow>
          <FormGroup>
            <Label htmlFor="startDateTime">Start Date & Time*</Label>
            <DateTimeInput
              type="datetime-local"
              id="startDateTime"
              name="startDateTime"
              value={formData.startDateTime ? new Date(formData.startDateTime).toISOString().slice(0, 16) : ''}
              onChange={(e) => handleDateTimeChange('startDateTime', e.target.value)}
              required
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="endDateTime">End Date & Time*</Label>
            <DateTimeInput
              type="datetime-local"
              id="endDateTime"
              name="endDateTime"
              value={formData.endDateTime ? new Date(formData.endDateTime).toISOString().slice(0, 16) : ''}
              onChange={(e) => handleDateTimeChange('endDateTime', e.target.value)}
              required
            />
          </FormGroup>
        </FormRow>
        
        <FormGroup>
          <Label htmlFor="location">Location*</Label>
          <Input
            type="text"
            id="location"
            name="location"
            value={formData.location}
            onChange={handleChange}
            placeholder="e.g., Online (Zoom) or Room H14, GUC Campus"
            required
          />
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="meetingLink">Meeting Link (for online events)</Label>
          <Input
            type="url"
            id="meetingLink"
            name="meetingLink"
            value={formData.meetingLink || ''}
            onChange={handleChange}
            placeholder="e.g., https://zoom.us/j/123456789"
          />
          <HelperText>Leave blank for in-person events</HelperText>
        </FormGroup>
        
        <FormRow>
          <FormGroup>
            <Label htmlFor="maxParticipants">Maximum Participants</Label>
            <Input
              type="number"
              id="maxParticipants"
              name="maxParticipants"
              value={formData.maxParticipants}
              onChange={handleChange}
              min="1"
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="status">Workshop Status</Label>
            <Select
              id="status"
              name="status"
              value={formData.status}
              onChange={handleChange}
            >
              <option value="upcoming">Upcoming</option>
              <option value="in-progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </Select>
          </FormGroup>
        </FormRow>
        
        <FormGroup>
          <Label htmlFor="registrationDeadline">Registration Deadline</Label>
          <DateTimeInput
            type="datetime-local"
            id="registrationDeadline"
            name="registrationDeadline"
            value={formData.registrationDeadline ? new Date(formData.registrationDeadline).toISOString().slice(0, 16) : ''}
            onChange={(e) => handleDateTimeChange('registrationDeadline', e.target.value)}
          />
        </FormGroup>
        
        <SectionTitle>Workshop Description</SectionTitle>
        
        <FormGroup>
          <Label htmlFor="shortDescription">Short Description*</Label>
          <Textarea
            id="shortDescription"
            name="shortDescription"
            value={formData.shortDescription}
            onChange={handleChange}
            placeholder="Brief description (1-2 sentences)"
            rows="2"
            required
          />
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="longDescription">Detailed Description</Label>
          <Textarea
            id="longDescription"
            name="longDescription"
            value={formData.longDescription}
            onChange={handleChange}
            placeholder="Detailed workshop description"
            rows="4"
          />
        </FormGroup>
        
        <SectionTitle>Workshop Speakers</SectionTitle>
        
        {formData.speakers.map((speaker, index) => (
          <ArrayItemContainer key={index}>
            {index > 0 && (
              <RemoveButton 
                type="button" 
                onClick={() => removeSpeaker(index)}
                title="Remove Speaker"
              >
                ×
              </RemoveButton>
            )}
            
            <FormGroup>
              <Label>Speaker Name*</Label>
              <Input
                type="text"
                value={speaker.name}
                onChange={(e) => handleSpeakerChange(index, 'name', e.target.value)}
                placeholder="e.g., Dr. Ahmed Mostafa"
                required
              />
            </FormGroup>
            
            <FormGroup>
              <Label>Speaker Title/Position</Label>
              <Input
                type="text"
                value={speaker.title}
                onChange={(e) => handleSpeakerChange(index, 'title', e.target.value)}
                placeholder="e.g., Senior Engineering Manager at Google"
              />
            </FormGroup>
            
            <FormGroup>
              <Label>Speaker Bio</Label>
              <Textarea
                value={speaker.bio}
                onChange={(e) => handleSpeakerChange(index, 'bio', e.target.value)}
                placeholder="Brief biography of the speaker"
                rows="3"
              />
            </FormGroup>
            
            <FormGroup>
              <Label>Speaker Image URL</Label>
              <Input
                type="url"
                value={speaker.image}
                onChange={(e) => handleSpeakerChange(index, 'image', e.target.value)}
                placeholder="URL to speaker's photo"
              />
            </FormGroup>
          </ArrayItemContainer>
        ))}
        
        <Button type="button" variant="secondary" onClick={addSpeaker}>
          + Add Another Speaker
        </Button>
        
        <SectionTitle>Workshop Agenda</SectionTitle>
        
        {formData.agenda.map((item, index) => (
          <ArrayItemContainer key={index}>
            {index > 0 && (
              <RemoveButton 
                type="button" 
                onClick={() => removeAgendaItem(index)}
                title="Remove Agenda Item"
              >
                ×
              </RemoveButton>
            )}
            
            <FormRow>
              <FormGroup>
                <Label>Time Slot</Label>
                <Input
                  type="text"
                  value={item.time}
                  onChange={(e) => handleAgendaChange(index, 'time', e.target.value)}
                  placeholder="e.g., 10:00 - 10:30"
                />
              </FormGroup>
              
              <FormGroup style={{ flex: '2' }}>
                <Label>Activity</Label>
                <Input
                  type="text"
                  value={item.activity}
                  onChange={(e) => handleAgendaChange(index, 'activity', e.target.value)}
                  placeholder="e.g., Welcome and Introduction"
                />
              </FormGroup>
            </FormRow>
          </ArrayItemContainer>
        ))}
        
        <Button type="button" variant="secondary" onClick={addAgendaItem}>
          + Add Agenda Item
        </Button>
        
        <SectionTitle>Workshop Materials</SectionTitle>
        
        {formData.materials.map((material, index) => (
          <ArrayItemContainer key={index}>
            {index > 0 && (
              <RemoveButton 
                type="button" 
                onClick={() => removeMaterial(index)}
                title="Remove Material"
              >
                ×
              </RemoveButton>
            )}
            
            <FormRow>
              <FormGroup>
                <Label>Material Name</Label>
                <Input
                  type="text"
                  value={material.name}
                  onChange={(e) => handleMaterialChange(index, 'name', e.target.value)}
                  placeholder="e.g., Interview Preparation Guide"
                />
              </FormGroup>
              
              <FormGroup style={{ flex: '2' }}>
                <Label>Material Link</Label>
                <Input
                  type="url"
                  value={material.link}
                  onChange={(e) => handleMaterialChange(index, 'link', e.target.value)}
                  placeholder="e.g., /materials/guide.pdf"
                />
              </FormGroup>
            </FormRow>
          </ArrayItemContainer>
        ))}
        
        <Button type="button" variant="secondary" onClick={addMaterial}>
          + Add Material
        </Button>
        
        <SectionTitle>Workshop Tags</SectionTitle>
        
        <FormGroup>
          <Label htmlFor="tags">Tags</Label>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Input
              type="text"
              id="tagInput"
              value={tagInput}
              onChange={handleTagInputChange}
              onKeyDown={handleTagKeyDown}
              placeholder="Enter a tag and press Enter"
            />
            <Button type="button" variant="secondary" onClick={addTag}>
              Add
            </Button>
          </div>
          <HelperText>Tags help students find relevant workshops</HelperText>
        </FormGroup>
        
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
          {formData.tags.map((tag, index) => (
            <div 
              key={index} 
              style={{ 
                background: 'rgba(33, 150, 243, 0.1)', 
                padding: '0.3rem 0.8rem',
                borderRadius: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              {tag}
              <span 
                style={{ cursor: 'pointer', fontWeight: 'bold' }} 
                onClick={() => removeTag(tag)}
              >
                ×
              </span>
            </div>
          ))}
        </div>
        
        <ActionButtons>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit">
            {isEditing ? 'Update Workshop' : 'Create Workshop'}
          </Button>
        </ActionButtons>
      </form>
    </FormContainer>
  );
};

export default WorkshopForm;